// Sync data-theme as early as the module graph allows (head theme-bootstrap.js still wins for FOUC).
import '@cast/shared-theme-data-access';

import './monitoring/sentry';

import { CssBaseline } from '@mui/material';
import { StyledEngineProvider } from '@mui/material/styles';
import { QueryClientProvider } from '@tanstack/react-query';
import { ReactQueryDevtools } from '@tanstack/react-query-devtools';
import dayjs from 'dayjs';
import duration from 'dayjs/plugin/duration';
import isSameOrAfter from 'dayjs/plugin/isSameOrAfter';
import isToday from 'dayjs/plugin/isToday';
import relativeTime from 'dayjs/plugin/relativeTime';
import timezone from 'dayjs/plugin/timezone';
import utc from 'dayjs/plugin/utc';
import { ModalProvider } from 'mui-modal-provider';
import { DndProvider } from 'react-dnd';
import { HTML5Backend } from 'react-dnd-html5-backend';
import { createRoot } from 'react-dom/client';
import {
  createBrowserRouter,
  createRoutesFromElements,
  Route,
  RouterProvider,
} from 'react-router-dom';

import { SnackbarsProvider } from '@cast/design-system';
import { addChatbotStateOverrides } from '@cast/features-organization-shared-opspilot';
import { RootErrorBoundary } from '@cast/shared-layout-ui';
import { attachAnalyticsCLI } from '@cast/analytics';
import {
  AccessControlProvider,
  AbilityProvider,
} from '@cast/infrastructure-abilities';

import { activateMockMiddleware } from 'core/api-middleware/mock-middleware/middlewares';

import { InitApp } from '@oneclick/layout/InitApp';
import { TrialProvider, TrialNudgeUserManager } from 'core/trial';
import { HubspotContactProvider } from 'core/hubspot';
import { ToolboxPanel } from 'core/dev-toolbox';
import { addAbilityOverrides } from 'core/dev-toolbox/features/ability-overrides/global-actions';
import { AgentationWrapper } from 'core/dev-toolbox/features/feedback-toolbar/AgentationWrapper';

import { CastctlConnectInstructionsProvider } from 'hooks/dialogs/cluster/components/CastctlOnboardingDialog';
import { CastctlConnectMethodTabs } from '@oneclick/features/connect-cluster/_components/CastctlConnectMethodTabs/CastctlConnectMethodTabs';

import { createJotaiRouter } from '@cast/infrastructure-navigation';
import { queryClient } from '@oneclick/core/react-query/client';
import { ThemeProvider } from '@oneclick/core/ThemeProvider';

import { App } from '@oneclick/App';
import { WaitForAbilities } from '@oneclick/layout/WaitForAbilities';
import { isE2E } from '@oneclick/utils/isE2E';

import './assets/fonts/fonts.css';
import '@cast/design-system/tokens.css';
import '@cast/design-system/typography.css';
import './app-tokens.css';

activateMockMiddleware();

dayjs.extend(utc);
dayjs.extend(timezone);
dayjs.extend(isSameOrAfter);
dayjs.extend(isToday);
dayjs.extend(relativeTime);
dayjs.extend(duration);

const router = createJotaiRouter(
  createBrowserRouter(
    createRoutesFromElements(
      <Route
        path="*"
        element={
          <DndProvider backend={HTML5Backend}>
            <QueryClientProvider client={queryClient}>
              <RootErrorBoundary>
                <SnackbarsProvider>
                  <InitApp initAuth initOrganization>
                    <AccessControlProvider useCurrentUser>
                      <AbilityProvider>
                        <WaitForAbilities>
                          <TrialProvider>
                            <HubspotContactProvider>
                              <ModalProvider>
                                <CastctlConnectInstructionsProvider
                                  value={CastctlConnectMethodTabs}
                                >
                                  <App />
                                </CastctlConnectInstructionsProvider>
                                <ToolboxPanel />
                                <AgentationWrapper />
                                <TrialNudgeUserManager />
                              </ModalProvider>
                            </HubspotContactProvider>
                          </TrialProvider>
                        </WaitForAbilities>
                      </AbilityProvider>
                    </AccessControlProvider>
                  </InitApp>
                </SnackbarsProvider>
              </RootErrorBoundary>
              {!isE2E() && (
                <ReactQueryDevtools
                  initialIsOpen={false}
                  buttonPosition="bottom-right"
                />
              )}
            </QueryClientProvider>
          </DndProvider>
        }
      />
    )
  )
);

createRoot(document.getElementById('root')!).render(
  <StyledEngineProvider injectFirst>
    <ThemeProvider>
      <CssBaseline />
      <RouterProvider router={router} />
    </ThemeProvider>
  </StyledEngineProvider>
);

attachAnalyticsCLI();

if (isE2E()) {
  addAbilityOverrides();
  addChatbotStateOverrides();
}
